/**
 * Browser agent with New Relic abstraction
 */

import assertNever from 'assert-never'

// Simple logger that does nothing
class SimpleLogger {
    info(_message: string, _data?: any): void {
        // Does nothing - just a placeholder
    }

    warn(_message: string, _data?: any): void {
        // Does nothing - just a placeholder
    }

    error(_message: string, _data?: any): void {
        // Does nothing - just a placeholder
    }

    debug(_message: string, _data?: any): void {
        // Does nothing - just a placeholder
    }
}

interface LogEntry {
    message: string
    level: 'info' | 'warn' | 'error' | 'debug'
    data?: any
}

class BrowserAgent {
    private initialized = false

    private initialize(): void {
        if (this.initialized) return

        // Wrap logger with New Relic if available
        // @ts-ignore
        if (typeof window !== 'undefined' && window.newrelic && window.newrelic.wrapLogger) {
            // @ts-ignore
            window.newrelic.wrapLogger(simpleLogger, 'info')
            // @ts-ignore
            window.newrelic.wrapLogger(simpleLogger, 'warn')
            // @ts-ignore
            window.newrelic.wrapLogger(simpleLogger, 'error')
            // @ts-ignore
            window.newrelic.wrapLogger(simpleLogger, 'debug')
        }

        this.initialized = true
    }

    trackEvent(type: string, context?: any): void {
        this.initialize()
        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && window.newrelic) {
                // @ts-ignore
                window.newrelic.addPageAction('UserAction', {
                    action: type,
                    timestamp: new Date().toISOString(),
                    ...context
                })
            } else {
                // eslint-disable-next-line no-console
                console.log('[Browser Agent] Event:', type, context)
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to track event:', error)
        }

        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && window.posthog) {
                // @ts-ignore
                window.posthog.capture(type, context)
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to track event in PostHog:', error)
        }
    }

    log(level: LogEntry['level'], message: string, data?: any): void {
        this.initialize()
        // console[level](message, data)
        try {
            // Call appropriate level method on simple logger
            switch (level) {
                case 'info':
                    simpleLogger.info(message, data)
                    break
                case 'warn':
                    simpleLogger.warn(message, data)
                    break
                case 'error':
                    simpleLogger.error(message, data)
                    break
                case 'debug':
                    simpleLogger.debug(message, data)
                    break
                default:
                    assertNever(level)
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to log:', error)
        }
    }

    trackYandexGoal(goal: string): void {
        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && typeof window.ym === 'function' && window.ymConfig?.counterId) {
                // @ts-ignore
                window.ym(window.ymConfig.counterId, 'reachGoal', goal)
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to track Yandex Metrika goal:', error)
        }
    }

    identify(userId: string, properties?: Record<string, any>): void {
        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && window.posthog) {
                // @ts-ignore
                window.posthog.identify(userId, properties)
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to identify user in PostHog:', error)
        }
    }

    reset(): void {
        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && window.posthog) {
                // @ts-ignore
                window.posthog.reset()
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to reset PostHog:', error)
        }
    }

    recordError(error: Error, context?: Record<string, any>): void {
        this.initialize()
        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && window.newrelic) {
                // @ts-ignore
                window.newrelic.noticeError(error, {
                    timestamp: new Date().toISOString(),
                    ...context
                })
            } else {
                // eslint-disable-next-line no-console
                console.error('[Browser Agent] Error:', error, context)
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to record error:', err)
        }

        try {
            // @ts-ignore
            if (typeof window !== 'undefined' && window.posthog) {
                // @ts-ignore
                window.posthog.captureException(error, context)
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to record error in PostHog:', err)
        }
    }
}

// Create simple logger instance
const simpleLogger = new SimpleLogger()

// Create singleton instance
export const browserAgent = new BrowserAgent()
